import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Check, ShieldCheck, X, Sparkles, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";
import { GBP, GBP2, fmtKwh, fmtKwp, statusLabel, fmtDateTimeGB } from "../lib/format";
import { useSettings, BrandLogo } from "../context/SettingsContext";

/**
 * Customer-facing public quote page (no auth, accessed via signed token link).
 * Repainted to match the Flow palette: white surfaces, deep indigo ink, teal
 * primary accent, peach warm accent. The Flow brand mark sits on a clean
 * indigo header bar that mirrors the PDF template — keeping the customer
 * experience consistent between web and print.
 */
export default function PublicQuote() {
  const { token } = useParams();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signerName, setSignerName] = useState("");
  const [showSign, setShowSign] = useState(false);
  const [showMgr, setShowMgr] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/quotes/${token}`);
      setData(r.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Quote not found"));
    }
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-10 text-center">
      <div>
        <div className="font-display text-3xl font-bold">Quote unavailable</div>
        <div className="muted mt-2 text-sm">{err}</div>
      </div>
    </div>
  );

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="muted font-mono text-xs uppercase tracking-widest">Loading…</div>
      </div>
    );
  }

  const { quote, project, design, org, images } = data;
  const orgName = settings?.org_name || org?.org_name || "Flow";

  const sign = async (endpoint) => {
    if (!signerName.trim()) { toast.error("Please type your full name"); return; }
    try {
      await api.post(`/public/quotes/${token}/${endpoint}`, { name: signerName.trim() });
      toast.success("Recorded");
      setShowSign(false); setShowMgr(false); setShowReject(false);
      setSignerName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="public-quote-page">
      {/* Indigo header bar — mirrors the PDF cover so customers feel a single brand. */}
      <header
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #314041 0%, #3E5052 55%, #00D6DF 130%)",
          borderBottom: "4px solid #00D6DF",
        }}
        data-testid="public-quote-header"
      >
        <div className="max-w-5xl mx-auto px-8 py-7 flex flex-col gap-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo size={40} />
              <div>
                <div className="font-display font-bold text-xl leading-tight">{orgName}</div>
                <div
                  className="uppercase tracking-[0.2em] text-[10px] mt-0.5"
                  style={{ color: "rgba(255,255,255,0.72)" }}
                >
                  Design · Quote · Install · Monitor
                </div>
              </div>
            </div>
            <div className="text-right">
              <div
                className="uppercase tracking-[0.2em] text-[10px]"
                style={{ color: "rgba(255,255,255,0.72)" }}
              >
                Solar PV Proposal
              </div>
              <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>
                Issued {fmtDateTimeGB(quote.created_at).split(",")[0]}
              </div>
            </div>
          </div>

          <div>
            <div
              className="uppercase tracking-[0.18em] text-[10px]"
              style={{ color: "rgba(255,255,255,0.72)" }}
            >
              {project?.postcode || "United Kingdom"}
            </div>
            <h1
              className="font-display text-4xl md:text-5xl font-bold leading-[1.05] mt-1"
              data-testid="public-quote-project-name"
            >
              {project?.name}
            </h1>
            <div className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.85)" }}>
              For <span className="font-semibold">{project?.customer_name}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-12 space-y-12">
        {/* KPI strip — total is teal, rest are surface */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="public-quote-kpis">
          <Stat label="System size" value={fmtKwp(design?.systemSize || 0)} />
          <Stat label="Annual yield" value={fmtKwh(design?.annualEnergyProduction || 0)} />
          <Stat label="Total inc. VAT" value={GBP.format(quote?.total_gbp || 0)} accent />
          <Stat
            label="Payback"
            value={quote?.financials?.payback_years ? `${quote.financials.payback_years} yr` : "—"}
          />
        </section>

        {/* PPA Savings teaser — only show when quote has PPA inputs */}
        {(quote?.ppa_rate_p_per_kwh != null && quote?.grid_rate_p_per_kwh != null) && (
          <section
            className="rounded-sm px-6 py-5 flex flex-wrap items-center justify-between gap-4"
            style={{
              background: "linear-gradient(135deg, #00D6DF 0%, #314041 100%)",
              color: "#fff",
            }}
            data-testid="public-quote-ppa-cta"
          >
            <div>
              <div className="uppercase tracking-[0.18em] text-[10px]" style={{ color: "rgba(255,255,255,0.78)" }}>
                Your PPA savings summary
              </div>
              <div className="font-display font-bold text-lg mt-1">
                Pay {quote.ppa_rate_p_per_kwh}p/kWh instead of {quote.grid_rate_p_per_kwh}p/kWh from the grid
              </div>
              <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>
                Download your personalised {quote.ppa_term_years || 25}-year savings projection — no CapEx, just savings.
              </div>
            </div>
            <a
              href={`${API}/public/quotes/${token}/pdf?variant=customer`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-semibold text-sm"
              style={{ background: "#fff", color: "#314041" }}
              data-testid="public-customer-pdf-btn"
            >
              <FileDown size={14} /> Download savings PDF
            </a>
          </section>
        )}

        <section>
          <div className="label mb-3" style={{ color: "var(--accent-hover)" }}>
            Scope of supply
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Qty</th>
                <th className="num">Unit (£)</th>
                <th className="num">Total (£)</th>
              </tr>
            </thead>
            <tbody>
              {(quote?.line_items || []).map((li, i) => (
                <tr key={i}>
                  <td>{li.description}</td>
                  <td className="num">{li.qty}</td>
                  <td className="num">{GBP2.format(li.unit_price_gbp)}</td>
                  <td className="num">{GBP2.format(li.qty * li.unit_price_gbp)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="num muted">Subtotal</td>
                <td className="num">{GBP2.format(quote?.subtotal_gbp || 0)}</td>
              </tr>
              <tr>
                <td colSpan={3} className="num muted">VAT @ {quote?.vat_pct}%</td>
                <td className="num">{GBP2.format(quote?.vat_gbp || 0)}</td>
              </tr>
              <tr
                style={{
                  background: "var(--warm-soft)",
                  borderTop: "2px solid var(--warm)",
                  borderBottom: "2px solid var(--warm)",
                }}
              >
                <td colSpan={3} className="num font-semibold" style={{ color: "var(--ink)" }}>
                  Total inc. VAT
                </td>
                <td
                  className="num font-display text-xl font-bold"
                  style={{ color: "var(--ink)" }}
                  data-testid="public-quote-total"
                >
                  {GBP2.format(quote?.total_gbp || 0)}
                </td>
              </tr>
            </tfoot>
          </table>
          {quote?.notes && <div className="mt-4 text-sm muted">{quote.notes}</div>}
        </section>

        <section>
          <div className="label mb-3" style={{ color: "var(--accent-hover)" }}>
            Lifetime financial summary
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Year-1 savings" value={GBP.format(quote?.financials?.annual_savings_gbp || 0)} />
            <Stat label="20-yr savings" value={GBP.format(quote?.financials?.lifetime_savings_gbp || 0)} warm />
            <Stat label="IRR" value={`${quote?.financials?.irr_pct?.toFixed?.(2) || 0}%`} />
            <Stat label="NPV" value={GBP.format(quote?.financials?.npv_gbp || 0)} />
          </div>
          <div className="text-xs muted mt-3 leading-relaxed">
            Based on {quote?.financials?.self_consumption_pct}% self-consumption · import @{" "}
            {quote?.financials?.import_tariff_p_per_kwh}p/kWh · SEG export @{" "}
            {quote?.financials?.seg_tariff_p_per_kwh}p/kWh · inflation {quote?.financials?.inflation_pct}%/yr
            · degradation {quote?.financials?.degradation_pct}%/yr.
          </div>
        </section>

        {(images || []).length > 0 && (
          <section data-testid="public-quote-images">
            <div className="label mb-3 flex items-center gap-1.5" style={{ color: "var(--accent-hover)" }}>
              <Sparkles size={11} /> Design renderings
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {images.map((img) => (
                <a
                  key={img.id}
                  href={img.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block border border-[var(--line)] rounded-md overflow-hidden hover-lift bg-white"
                  data-testid={`public-quote-img-${img.id}`}
                >
                  <img src={img.url} alt={img.name} className="w-full h-48 object-cover" loading="lazy" />
                  <div className="text-[11px] muted px-3 py-2 bg-[var(--surface)] border-t border-[var(--line-soft)]">
                    {img.name}
                  </div>
                </a>
              ))}
            </div>
            <div className="text-xs muted mt-3">
              Imported from the SolarEdge Designer proposal. Click any image to view full size.
            </div>
          </section>
        )}

        <section
          data-testid="public-quote-signoff"
          className="rounded-md"
          style={{
            background: "var(--surface)",
            borderLeft: "4px solid var(--accent)",
            padding: "26px 28px",
          }}
        >
          <div className="label" style={{ color: "var(--accent-hover)" }}>Approvals</div>
          <h2 className="font-display text-2xl font-semibold mb-6 mt-1">Sign-off</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="bg-white p-6 rounded-md border border-[var(--line)]"
              data-testid="customer-signoff-card"
            >
              <div className="label">Customer</div>
              {quote.customer_signed_at ? (
                <>
                  <div className="font-display text-xl mt-2 flex items-center gap-2">
                    <Check size={18} className="text-[var(--ok)]" /> {quote.customer_signature_name}
                  </div>
                  <div className="muted text-xs mt-0.5">{fmtDateTimeGB(quote.customer_signed_at)}</div>
                </>
              ) : (
                <>
                  <p className="muted text-sm mt-2">
                    By signing, you accept the scope, pricing and assumptions in this proposal.
                  </p>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button className="btn-accent" onClick={() => setShowSign(true)} data-testid="customer-sign-btn">
                      <Check size={13} /> Accept proposal
                    </button>
                    <button
                      className="btn-ghost"
                      onClick={() => setShowReject(true)}
                      data-testid="customer-reject-btn"
                    >
                      <X size={13} /> Decline
                    </button>
                  </div>
                </>
              )}
            </div>

            <div
              className="bg-white p-6 rounded-md border border-[var(--line)]"
              data-testid="manager-signoff-card"
            >
              <div className="label">Project manager</div>
              {quote.manager_signed_at ? (
                <>
                  <div className="font-display text-xl mt-2 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-[var(--ok)]" /> {quote.manager_signature_name}
                  </div>
                  <div className="muted text-xs mt-0.5">{fmtDateTimeGB(quote.manager_signed_at)}</div>
                </>
              ) : quote.customer_signed_at ? (
                <>
                  <p className="muted text-sm mt-2">
                    Awaiting your final sign-off. Once approved the EPC team will be authorised to mobilise.
                  </p>
                  <button className="btn-primary mt-4" onClick={() => setShowMgr(true)} data-testid="manager-sign-btn">
                    <ShieldCheck size={14} /> Authorise installation
                  </button>
                </>
              ) : (
                <p className="muted text-sm mt-2">
                  Manager sign-off becomes available after the customer accepts the proposal.
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 text-xs muted">
            Quote status: <span className="font-mono">{statusLabel(quote.status)}</span> ·{" "}
            {fmtDateTimeGB(quote.expires_at)} expiry
          </div>
        </section>

        <footer
          className="border-t-2 pt-5 mt-6 flex items-end justify-between flex-wrap gap-3"
          style={{ borderColor: "var(--accent)" }}
        >
          <div>
            <div className="font-semibold text-sm" style={{ color: "var(--ink)" }}>{orgName}</div>
            <div className="muted text-xs">
              {org?.org_address} · VAT {org?.org_vat_number || "—"} · Reg {org?.org_reg_number || "—"}
            </div>
          </div>
          <div className="uppercase tracking-[0.2em] text-[9px] muted">
            Design · Quote · Install · Monitor
          </div>
        </footer>
      </main>

      {showSign && (
        <SignDialog
          title="Accept proposal"
          submitLabel="Sign & accept"
          tone="accent"
          signerName={signerName}
          setSignerName={setSignerName}
          onSubmit={() => sign("sign")}
          onClose={() => setShowSign(false)}
          testid="customer-sign-dialog"
        />
      )}
      {showMgr && (
        <SignDialog
          title="Authorise installation"
          submitLabel="Authorise"
          tone="ink"
          signerName={signerName}
          setSignerName={setSignerName}
          onSubmit={() => sign("manager-sign")}
          onClose={() => setShowMgr(false)}
          testid="manager-sign-dialog"
        />
      )}
      {showReject && (
        <SignDialog
          title="Decline proposal"
          submitLabel="Decline"
          tone="warm"
          signerName={signerName}
          setSignerName={setSignerName}
          onSubmit={() => sign("reject")}
          onClose={() => setShowReject(false)}
          testid="reject-dialog"
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent, warm }) {
  // Default = surface tile, accent = teal (used for headline total), warm = peach tile
  const isAccent = !!accent;
  const isWarm = !!warm;
  const bg = isAccent ? "var(--accent)" : isWarm ? "var(--warm-soft)" : "var(--surface)";
  const border = isAccent ? "var(--accent)" : isWarm ? "var(--warm)" : "var(--line)";
  const textColour = isAccent ? "#FFFFFF" : "var(--ink)";
  const labelColour = isAccent ? "rgba(255,255,255,0.78)" : "var(--accent-hover)";
  return (
    <div
      className="p-5 rounded-md border"
      style={{ background: bg, borderColor: border, color: textColour }}
    >
      <div
        className="uppercase tracking-[0.14em] text-[9px] font-bold"
        style={{ color: labelColour }}
      >
        {label}
      </div>
      <div className="font-display text-2xl md:text-3xl font-bold mt-2 leading-none">{value}</div>
    </div>
  );
}

function SignDialog({ title, submitLabel, tone = "accent", signerName, setSignerName, onSubmit, onClose, testid }) {
  const borderColour =
    tone === "warm" ? "var(--warm)" :
    tone === "ink" ? "var(--ink)" :
    "var(--accent)";
  return (
    <div className="modal-overlay" onClick={onClose} data-testid={testid}>
      <div
        className="modal-card p-8"
        onClick={(e) => e.stopPropagation()}
        style={{ borderTop: `4px solid ${borderColour}` }}
      >
        <div className="font-display text-2xl font-bold mb-1" style={{ color: "var(--ink)" }}>
          {title}
        </div>
        <div className="label mb-5" style={{ color: "var(--accent-hover)" }}>
          Type your full legal name to record this approval
        </div>
        <label className="label block mb-1.5" style={{ color: "var(--ink-2)" }}>Full name</label>
        <input
          autoFocus
          className="input-swiss mb-6"
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="e.g. Jane A. Smith"
          data-testid="sign-name-input"
        />
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} data-testid="sign-cancel-btn">Cancel</button>
          <button
            className={tone === "warm" ? "btn-ghost" : "btn-accent"}
            onClick={onSubmit}
            data-testid="sign-confirm-btn"
            style={tone === "warm" ? { borderColor: "var(--warm)", color: "var(--warm-hover)" } : undefined}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
